import { TAILWIND_MD_MIN_PX } from '@/shared/constants/breakpoints';
import { AppLink } from '@/shared/components/AppLink';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';

function isExternalLink(url: string) {
  return /^https?:\/\//i.test(url);
}

interface StaticBannerSectionProps {
  data: Record<string, unknown> & {
    title?: string[];
    showTitle?: boolean;
    imageUrl?: string;
    desktopImageUrl?: string;
    link?: string;
    lazyload?: string;
    blankImageUrl?: string;
    shouldRefresh?: boolean;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function StaticBannerSection({
  data,
  sectionKey: _sectionKey,
  eventLabel,
  priority: _priority,
}: StaticBannerSectionProps) {
  const { analytics, isInitialized } = useAnalytics();
  const title = (data.title || []) as string[];
  const showTitle = data.showTitle === true;
  const mobileImageUrl = data.imageUrl || data.desktopImageUrl || '';
  const desktopImageUrl = data.desktopImageUrl || data.imageUrl || '';
  const hasDesktopImage = Boolean(data.desktopImageUrl);
  const link = data.link || '#';
  const lazyload = data.lazyload;

  if (!mobileImageUrl) return null;

  const ariaLabel = title.length ? title.join(' ') : 'Banner';

  const loadingMode = lazyload ? 'lazy' : 'eager';

  const Image = hasDesktopImage ? (
    <picture>
      <source media={`(min-width: ${TAILWIND_MD_MIN_PX}px)`} srcSet={desktopImageUrl} />
      <img
        src={mobileImageUrl}
        alt={ariaLabel}
        className="w-full h-auto block"
        loading={loadingMode}
        decoding="async"
      />
    </picture>
  ) : (
    <img
      src={mobileImageUrl}
      alt={ariaLabel}
      className="w-full h-auto block"
      loading={loadingMode}
      decoding="async"
    />
  );

  const clickEventName = `${(eventLabel || 'Static Banner').trim()} Clicked`;
  const handleClick = () => {
    if (!analytics || !isInitialized) return;
    try {
      // No custom attributes (commonAttributes still apply globally)
      analytics.trackEvent(clickEventName);
    } catch (error) {
      console.error(`Error tracking "${clickEventName}":`, error);
    }
  };

  const Clickable = (
    isExternalLink(link) ? (
      <a
        href={link}
        className="block"
        title={title?.[0] || 'Banner'}
        aria-label={ariaLabel}
        onClick={handleClick}
        rel="noopener noreferrer"
      >
        {Image}
      </a>
    ) : (
      <AppLink
        to={link}
        className="block"
        title={title?.[0] || 'Banner'}
        aria-label={ariaLabel}
        onClick={handleClick}
        shouldRefresh={data.shouldRefresh}
      >
        {Image}
      </AppLink>
    )
  );

  return (
    <section className="py-4 bg-white">
      <div className="container-fluid md:px-0">
        {showTitle && title.length > 0 && (
          <h2 className="mb-4 text-base md:text-[24px] font-normal text-center leading-normal md:leading-[30px]">
            <span className="font-primary text-[#9D7792] md:text-[#032C5E]">
              {title[0]}
              {title[1] ? (
                <>
                  <br />
                  {title[1]}
                </>
              ) : null}
            </span>
          </h2>
        )}

        {/* Full-bleed on mobile only: cancel container-fluid padding (30px) */}
        <div className="-mx-[30px] md:mx-0">{Clickable}</div>
      </div>
    </section>
  );
}


